// Outbox transaksi offline (US-OFFLINE-1/2, NFR-OFFLINE-1) — antrian lokal
// SaleRequest yang gagal terkirim saat sinyal putus. Disinkron ke /sync/sales
// saat online; idempoten via UUID (server mengabaikan duplikat) sehingga
// flush berulang aman & tak menggandakan transaksi. Pakai localStorage
// (konsisten dengan hold) — payload transaksi kecil.

import { useSyncExternalStore } from "react";
import { syncSales, type SaleRequest } from "../api";

const KEY = "reza:pos:outbox:v1";
const listeners = new Set<() => void>();

function read(): SaleRequest[] {
  try {
    const raw = localStorage.getItem(KEY);
    return raw ? (JSON.parse(raw) as SaleRequest[]) : [];
  } catch {
    return [];
  }
}

function write(list: SaleRequest[]) {
  localStorage.setItem(KEY, JSON.stringify(list));
  listeners.forEach((fn) => fn());
}

// enqueueSale: simpan transaksi ke antrian. Idempoten terhadap id (UUID) agar
// retry checkout tak menggandakan.
export function enqueueSale(sale: SaleRequest) {
  const list = read();
  if (list.some((s) => s.id === sale.id)) return;
  list.push(sale);
  write(list);
}

export function outboxSales(): SaleRequest[] {
  return read();
}

export function outboxCount(): number {
  return read().length;
}

let flushing = false;

// flushOutbox: kirim seluruh antrian ke server; hapus yang sukses. Yang gagal
// tetap di antrian untuk dicoba lagi. Return jumlah tersisa. Aman dipanggil
// berulang/paralel (guard `flushing`).
export async function flushOutbox(token: string): Promise<number> {
  if (flushing || !token) return outboxCount();
  const pending = read();
  if (pending.length === 0) return 0;
  flushing = true;
  try {
    const res = await syncSales(token, pending);
    const okIds = new Set(res.results.filter((r) => !r.error).map((r) => r.id));
    // Baca ulang: transaksi baru bisa masuk antrian selama flush berjalan.
    const remaining = read().filter((s) => !okIds.has(s.id));
    write(remaining);
    return remaining.length;
  } catch {
    return outboxCount(); // server tak terjangkau — biarkan, coba lagi nanti.
  } finally {
    flushing = false;
  }
}

function subscribe(fn: () => void) {
  listeners.add(fn);
  window.addEventListener("storage", fn); // sinkron antar-tab.
  return () => {
    listeners.delete(fn);
    window.removeEventListener("storage", fn);
  };
}

// useOutboxCount: jumlah transaksi menunggu sinkron (reaktif untuk badge).
export function useOutboxCount(): number {
  return useSyncExternalStore(subscribe, outboxCount, () => 0);
}
